import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconAlertTriangle,
  IconBell,
  IconInfoCircle,
  IconUsers,
} from '@tabler/icons-react';

export default function AnnouncementDetail() {
  const { language } = useTranslation();
  const { announcementId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !announcementId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'announcements', announcementId));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setAnnouncement(data);
          // Mark as read
          if (userProfile?.id && !data.reads?.includes(userProfile.id)) {
            await updateDoc(doc(db, 'organisations', orgId, 'announcements', announcementId), {
              reads: arrayUnion(userProfile.id),
            });
          }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, announcementId, userProfile?.id]);

  const handleAcknowledge = async () => {
    if (!userProfile?.id) return;
    setAcknowledging(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'announcements', announcementId), {
        acknowledgements: arrayUnion({
          userId: userProfile.id,
          name: userProfile.name,
          acknowledgedAt: new Date().toISOString(),
        }),
      });
      setAnnouncement(prev => ({
        ...prev,
        acknowledgements: [...(prev.acknowledgements || []), {
          userId: userProfile.id,
          name: userProfile.name,
          acknowledgedAt: new Date().toISOString(),
        }],
      }));
    } catch (err) { console.error(err); }
    setAcknowledging(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const priorityIcon = (priority) => {
    if (priority === 'urgent') return <IconAlertTriangle size={20} color="var(--color-danger)" stroke={1.5} />;
    if (priority === 'important') return <IconBell size={20} color="var(--color-warning)" stroke={1.5} />;
    return <IconInfoCircle size={20} color="var(--color-brand)" stroke={1.5} />;
  };

  const priorityColour = (priority) => {
    if (priority === 'urgent') return 'var(--color-danger)';
    if (priority === 'important') return 'var(--color-warning)';
    return 'var(--color-brand)';
  };

  const hasAcknowledged = announcement?.acknowledgements?.some(a => a.userId === userProfile?.id);

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!announcement) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/announcements')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Announcements
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Announcement not found</div>
      </div>
    </Layout>
  );

  const colour = priorityColour(announcement.priority);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 640, margin: '0 auto' }}>

        <div onClick={() => navigate('/announcements')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Announcements
        </div>

        {/* Header card */}
        <Card style={{ marginBottom: 16, border: '0.5px solid ' + colour, background: colour + '08' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              {priorityIcon(announcement.priority)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 400, color: colour, marginBottom: 8 }}>
                {announcement.title}
              </div>
              <div style={{ fontSize: 14, color: 'var(--color-text-primary)', lineHeight: 1.7, marginBottom: 16 }}>
                {announcement.message}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--color-text-tertiary)', flexWrap: 'wrap' }}>
                <span>Sent by {announcement.sentBy}</span>
                <span>{formatDateTime(announcement.sentAt)}</span>
                <span style={{ textTransform: 'capitalize', padding: '1px 8px', borderRadius: 20, background: colour + '18', color: colour, fontSize: 11 }}>
                  {announcement.priority}
                </span>
                {announcement.targetAudience && announcement.targetAudience !== 'all' && (
                  <span style={{ textTransform: 'capitalize', padding: '1px 8px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)', fontSize: 11 }}>
                    {announcement.targetAudience} only
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Acknowledgement required */}
        {announcement.requiresAcknowledgement && !hasAcknowledged && (
          <div style={{ background: colour + '12', border: '0.5px solid ' + colour, borderRadius: 'var(--radius-lg)', padding: '16px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: colour, marginBottom: 12 }}>
              This announcement requires your acknowledgement
            </div>
            <Button
              variant="primary"
              icon={<IconCheck size={14} stroke={1.5} />}
              onClick={handleAcknowledge}
              disabled={acknowledging}
              loading={acknowledging}
            >
              {acknowledging ? 'Confirming...' : 'I acknowledge this announcement'}
            </Button>
          </div>
        )}

        {hasAcknowledged && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'var(--color-success-light)', border: '0.5px solid var(--color-success)', borderRadius: 'var(--radius-lg)', marginBottom: 16 }}>
            <IconCheck size={16} color="var(--color-success)" stroke={2} />
            <span style={{ fontSize: 13, color: 'var(--color-success)' }}>You have acknowledged this announcement</span>
          </div>
        )}

        {/* Read receipts & acknowledgements — managers only */}
        {(role === 'org_admin' || role === 'manager') && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <IconUsers size={16} color="var(--color-text-tertiary)" stroke={1.5} />
                <div style={{ fontSize: 14, fontWeight: 400 }}>
                  Read by ({announcement.reads?.length || 0})
                </div>
              </div>
              {!announcement.reads?.length ? (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No reads yet</div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {announcement.reads.length} staff member{announcement.reads.length !== 1 ? 's' : ''} have read this
                </div>
              )}
            </Card>

            {announcement.requiresAcknowledgement && (
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <IconCheck size={16} color="var(--color-success)" stroke={1.5} />
                  <div style={{ fontSize: 14, fontWeight: 400 }}>
                    Acknowledged ({announcement.acknowledgements?.length || 0})
                  </div>
                </div>
                {!announcement.acknowledgements?.length ? (
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No acknowledgements yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {announcement.acknowledgements.slice(0, 5).map((a, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={a.name} size="xs" />
                        <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{a.name}</div>
                      </div>
                    ))}
                    {announcement.acknowledgements.length > 5 && (
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                        +{announcement.acknowledgements.length - 5} more
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}